import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { checkDuplicatePhoneCount, fetchNewOrders, updateOrderStatus, updateFollowUpCheckpoint, deleteOrder, fetchNewOrdersTotals, fetchNewOrdersSummary, fetchFollowUpCheckpointCounts, sendSecondaryPhoneConfirmation, sendPrimaryPhoneConfirmation, sendPrimaryPhoneManualTemplate } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { formatPhoneWithCaseStyle, teePlusPackedItemData, orderHasBlackEdges } from "../../utils/orderLineDisplay";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import OrderMobileCardList from "../../Components/orders/OrderMobileCardList";
import OrderPaymentBadge, { isFulfillmentStatus, isPaidOnline, orderCanEnterFulfillment, unpaidFulfillmentMessage } from "../../Components/orders/OrderPaymentBadge";
import OrderFollowUpTimeline, { normalizeFollowUpCheckpoint, FOLLOW_UP_CHECKPOINTS, FOLLOW_UP_CHECKPOINT_VALUES } from "../../Components/orders/OrderFollowUpTimeline";
import StartConversationButton from "../../Components/orders/StartConversationButton";
import ViewConversationButton from "../../Components/orders/ViewConversationButton";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  FileText,
  Copy,
  Download,
  Link as LinkIcon,
  MoreVertical,
  Edit,
  Trash2,
  Archive,
  Clock,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
  RefreshCw,
  MessageSquare,
  Truck,
  AlertCircle,
  Layers,
  CopyCheck,
  Eye,
  EyeOff,
  Filter,
  X,
  Save,
  Plus,
  Minus,
  Search,
  Upload,
  DownloadCloud,
  Printer,
  Share2,
  Flag,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  Info,
  Settings,
  LogOut,
  Menu,
  XCircle,
  RotateCcw,
  UserCog,
  Shield,
  Loader,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Building2,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const hasValidSecondaryPhone = (phoneNumber2) =>
  phoneNumber2 &&
  phoneNumber2 !== "غير معروف" &&
  phoneNumber2 !== "Unknown Phone";

const SecondaryWhatsAppButton = ({ order, sendingId, onSend, className = "", blocked = false }) => {
  if (!hasValidSecondaryPhone(order.phoneNumber2)) return null;

  const isSending = sendingId === order.orderNumber;

  return (
    <button
      type="button"
      onClick={() => onSend(order)}
      disabled={isSending || blocked}
      title={blocked ? "Payment Pending" : "إرسال قالب تأكيد WhatsApp (83272) للرقم التاني"}
      className={`bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium transition-all ${className}`}
    >
      <div className="flex items-center justify-center gap-1">
        <MessageSquare size={14} />
        {isSending ? "جاري الإرسال..." : "رساله التأكيد للرقم التاني"}
      </div>
    </button>
  );
};

const PrimaryManualTemplateButton = ({ order, sendingId, onSend, className = "", blocked = false }) => {
  if (!order.phoneNumber || order.phoneNumber === "Unknown Phone") return null;

  const isSending = sendingId === order.orderNumber;

  return (
    <button
      type="button"
      onClick={() => onSend(order)}
      disabled={isSending || blocked}
      title={blocked ? "Payment Pending" : "إرسال قالب WhatsApp (82960) للرقم الأول"}
      className={`bg-sky-600 text-white px-3 py-2 rounded-lg hover:bg-sky-700 disabled:opacity-50 text-sm font-medium transition-all ${className}`}
    >
      <div className="flex items-center justify-center gap-1">
        <MessageSquare size={14} />
        {isSending ? "جاري الإرسال..." : "اضغط علي تأكيد الطلب"}
      </div>
    </button>
  );
};

const PrimaryWhatsApp1199Button = ({ order, sendingId, onSend, className = "", blocked = false }) => {
  if (!order.phoneNumber || order.phoneNumber === "Unknown Phone") return null;

  const isSending = sendingId === order.orderNumber;

  return (
    <button
      type="button"
      onClick={() => onSend(order)}
      disabled={isSending || blocked}
      title={blocked ? "Payment Pending" : "إرسال قالب تأكيد WhatsApp (83272) للرقم الأول — يدوي"}
      className={`bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-all ${className}`}
    >
      <div className="flex items-center justify-center gap-1">
        <MessageSquare size={14} />
        {isSending ? "جاري الإرسال..." : "رساله التأكيد للرقم الاساسي"}
      </div>
    </button>
  );
};

const NewOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [followUpFilter, setFollowUpFilter] = useState(null);
  const [followUpCounts, setFollowUpCounts] = useState({ total: 0, counts: {} });
  const [isLoadingFollowUpCounts, setIsLoadingFollowUpCounts] = useState(false);
  const [sendingSecondaryWa, setSendingSecondaryWa] = useState(null);
  const [sendingPrimaryTemplate1521, setSendingPrimaryTemplate1521] = useState(null);
  const [sendingPrimaryWa1199, setSendingPrimaryWa1199] = useState(null);

  const toggleExpand = (orderNumber) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalCount, setTotalCount] = useState(0);
  

  const { data: totals, isLoading: isLoadingTotals, error: errorTotals } = useQuery({
    queryKey: ["newOrdersTotals"],
    queryFn: fetchNewOrdersTotals,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });

  const { data: newOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["newOrdersSummary"],
    queryFn: fetchNewOrdersSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate, followUpFilter, sortOrder]);

  useEffect(() => {
    if (!showFilters) return;
    let cancelled = false;
    const loadCounts = async () => {
      setIsLoadingFollowUpCounts(true);
      try {
        const data = await fetchFollowUpCheckpointCounts("New");
        if (!cancelled) setFollowUpCounts(data);
      } catch (err) {
        console.error("Error loading follow-up counts:", err);
      } finally {
        if (!cancelled) setIsLoadingFollowUpCounts(false);
      }
    };
    loadCounts();
    return () => { cancelled = true; };
  }, [showFilters, loadedOrders.length]);

  const isLoading = isLoadingTotals || isLoadingSummary;
  const queryError = errorTotals || errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        // toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const handleCopyPhoneNumber = (phoneNumber) => {
    copyToClipboard(phoneNumber);
    toast.success(`تم نسخ الرقم: ${phoneNumber}`);
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        // toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "New",
          paymentMethod: order.paymentMethod || "COD",
          paymentStatus: order.paymentStatus || "NotRequired",
          paymentTransactionId: order.paymentTransactionId || "",
          followUpCheckpoint: normalizeFollowUpCheckpoint(order.followUpCheckpoint),
        };
      })
    );
  };

const loadInitialOrders = async () => {
  setIsOrdersLoading(true);
  setCurrentPage(0);
  setHasMore(true);
  try {
    const data = await fetchNewOrders(0, pageSize, followUpFilter, sortOrder);
    if (data.length === 0 || data.length < pageSize) {
      setHasMore(false);
    }
    const enrichedOrders = await enrichOrders(data);
    setLoadedOrders(enrichedOrders);
    setCurrentPage(1);
    setTotalCount(enrichedOrders.length);
  } catch (err) {
    console.error("Error loading initial orders:", err);
    setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
    toast.error("فشل في تحميل الطلبات!");
  } finally {
    setIsOrdersLoading(false);
  }
};

const loadMoreOrders = async () => {
  setIsLoadingMore(true);
  try {
    const data = await fetchNewOrders(currentPage, pageSize, followUpFilter, sortOrder);
    if (data.length === 0 || data.length < pageSize) {
      setHasMore(false);
    }
    const enrichedOrders = await enrichOrders(data);
    setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
    setCurrentPage((prev) => prev + 1);
    setTotalCount((prev) => prev + enrichedOrders.length);
  } catch (err) {
    console.error("Error loading more orders:", err);
    setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
    toast.error("فشل في تحميل المزيد من الطلبات!");
  } finally {
    setIsLoadingMore(false);
  }
};

  // دالة جديدة لإعادة تحميل الصفحة بحجم جديد
const handlePageSizeChange = async (newSize) => {
  setPageSize(newSize);
  setCurrentPage(0);
  setIsOrdersLoading(true);
  try {
    const data = await fetchNewOrders(0, newSize, followUpFilter, sortOrder);
    const enrichedOrders = await enrichOrders(data);
    setLoadedOrders(enrichedOrders);
    setCurrentPage(1);
    setHasMore(data.length === newSize); // لو جاب العدد الكامل يبقى لسه في المزيد
    setTotalCount(enrichedOrders.length);
  } catch (err) {
    console.error("Error changing page size:", err);
    toast.error("فشل في تحميل الطلبات!");
  } finally {
    setIsOrdersLoading(false);
  }
};

  const rejectUnpaidFulfillment = (order) => {
    if (orderCanEnterFulfillment(order)) return false;
    toast.error(unpaidFulfillmentMessage(order));
    return true;
  };

  const handleUpdateStatus = async (orderId, newStatus, order) => {
    if (!canAct) return;
    const targetOrder = order || loadedOrders.find((item) => item.orderNumber === orderId);
    if (newStatus === "Confirmed" && orderHasBlackEdges(targetOrder)) {
      newStatus = "TeePlus";
    }
    if (isFulfillmentStatus(newStatus) && rejectUnpaidFulfillment(targetOrder || { orderNumber: orderId })) return;
    if (isPaidOnline(targetOrder) && (newStatus === "Deposit Required" || newStatus === "Deposit" || newStatus === "Custom" || newStatus === "Custom Order")) {
      toast.error(`Order #${orderId} is already paid online. A deposit is not required.`);
      return;
    }
    let reason = newStatus === "TeePlus" && orderHasBlackEdges(targetOrder)
      ? "حواف سوداء: تحويل تلقائي إلى TeePlus عند التأكيد"
      : "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");

      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const syncFollowUpCheckpointState = (orderId, checkpoint) => {
    const normalized = normalizeFollowUpCheckpoint(checkpoint);
    setLoadedOrders((prev) => {
      if (followUpFilter && normalized !== followUpFilter) {
        return prev.filter((order) => order.orderNumber !== orderId);
      }
      return prev.map((order) =>
        order.orderNumber === orderId
          ? { ...order, followUpCheckpoint: normalized }
          : order
      );
    });
    if (showFilters) {
      fetchFollowUpCheckpointCounts("New").then(setFollowUpCounts).catch(() => {});
    }
  };

  const handleFollowUpCheckpointUpdate = async (orderId, checkpoint) => {
    if (!canAct) return;
    if (!canAct) return;
    try {
      await updateFollowUpCheckpoint(orderId, checkpoint);
      syncFollowUpCheckpointState(orderId, checkpoint);
      toast.success("تم تحديث مرحلة المتابعة");
    } catch (err) {
      toast.error(err.message || "فشل تحديث مرحلة المتابعة");
      throw err;
    }
  };

  const handleSendSecondaryPhoneConfirmation = async (order) => {
    if (!canAct || rejectUnpaidFulfillment(order)) return;
    if (!hasValidSecondaryPhone(order.phoneNumber2)) {
      toast.error("لا يوجد رقم تاني صالح لهذا الطلب");
      return;
    }

    const confirmed = window.confirm(
      `إرسال قالب تأكيد WhatsApp (83272) للرقم التاني؟\n\n${order.phoneNumber2}\n\n` +
      "العميل يقدر يؤكد أو يلغي من الأزرار — نفس flow التأكيد العادي."
    );
    if (!confirmed) return;

    setSendingSecondaryWa(order.orderNumber);
    try {
      const data = await sendSecondaryPhoneConfirmation(order.orderNumber);
      if (data.success) {
        syncFollowUpCheckpointState(
          order.orderNumber,
          data.followUpCheckpoint || FOLLOW_UP_CHECKPOINT_VALUES.WAITING_FOR_SECONDARY
        );
        toast.success(data.message || "تم إرسال قالب التأكيد للرقم التاني");
      } else {
        toast.warning(data.message || "فشل إرسال القالب");
      }
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingSecondaryWa(null);
    }
  };

  const handleSendPrimaryPhoneManualTemplate = async (order) => {
    if (!canAct || rejectUnpaidFulfillment(order)) return;
    if (!order.phoneNumber || order.phoneNumber === "Unknown Phone") {
      toast.error("لا يوجد رقم أول صالح لهذا الطلب");
      return;
    }

    const confirmed = window.confirm(
      `إرسال قالب WhatsApp (82960) للرقم الأول؟\n\n${order.phoneNumber}`
    );
    if (!confirmed) return;

    setSendingPrimaryTemplate1521(order.orderNumber);
    try {
      const data = await sendPrimaryPhoneManualTemplate(order.orderNumber);
      if (data.success) {
        syncFollowUpCheckpointState(
          order.orderNumber,
          data.followUpCheckpoint || FOLLOW_UP_CHECKPOINT_VALUES.WAITING_FOR_TAP
        );
        toast.success(data.message || "تم إرسال القالب للرقم الأول");
      } else {
        toast.warning(data.message || "فشل إرسال القالب");
      }
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingPrimaryTemplate1521(null);
    }
  };

  const handleSendPrimaryPhoneConfirmation = async (order) => {
    if (!canAct || rejectUnpaidFulfillment(order)) return;
    if (!order.phoneNumber || order.phoneNumber === "Unknown Phone") {
      toast.error("لا يوجد رقم أول صالح لهذا الطلب");
      return;
    }

    const confirmed = window.confirm(
      `إرسال قالب تأكيد WhatsApp (83272) للرقم الأول؟\n\n${order.phoneNumber}\n\n` +
      "العميل يقدر يؤكد أو يلغي من الأزرار — نفس flow التأكيد العادي."
    );
    if (!confirmed) return;

    setSendingPrimaryWa1199(order.orderNumber);
    try {
      const data = await sendPrimaryPhoneConfirmation(order.orderNumber);
      if (data.success) {
        toast.success(data.message || "تم إرسال قالب التأكيد للرقم الأول");
      } else {
        toast.warning(data.message || "فشل إرسال القالب");
      }
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingPrimaryWa1199(null);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleCopyMessage = (order) => {
    const itemsList = order.items.map(item => `النوع: ${item.type} - الكود: ${item.code}`).join('\n');

    const messages = [
      `أهلاً أ/ "${order.name}" 🌸
مع حضرتك فاطمة من فريق جرابتكم ستور 💛
بنراجع معاك تفاصيل الطلب قبل ما نبدأ التنفيذ، لأن كل تصميم بيتعمل مخصوص لك 🎨✨

تفاصيل الطلب:
${itemsList}

بمجرد تأكيدك هنبدأ فورًا في التجهيز والشحن ⚡🚚

لتأكيد الطلب: رد بـ (نعم - تأكيد) ✅
لو في تعديل أو إلغاء، قولنا قبل البدء 💬
شكرًا لثقتك فينا 🤍🌷`,
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    copyToClipboard(randomMessage);
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const handleCopyAllExcelData = async (order) => {
    if (rejectUnpaidFulfillment(order)) return;
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const paymentColumn = order.paymentMethod === "Online" && (order.paymentStatus === "Paid" || order.paymentStatus === "PartiallyRefunded")
        ? "Prepaid"
        : "COD";
      const customerBase = `${currentReference}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\t${paymentColumn}\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`${currentReference}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      await copyOrderToTeePlus(finalText, order.orderNumber);

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const isOrderNonCustom = (order) => {
    return !order.items.some(item => item.code && item.code.startsWith("CUSTOM"));
  };

  const handleCopyAllVisibleOrders = async () => {
    const payableOrders = loadedOrders.filter(orderCanEnterFulfillment);
    if (payableOrders.length < loadedOrders.length) {
      toast.warn("تم تخطي الطلبات الأونلاين غير المدفوعة. Payment Pending");
    }
    const nonCustomOrders = payableOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
      return;
    }

    const confirm = window.confirm(
      `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
    );
    if (!confirm) return;

    let finalLines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of nonCustomOrders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );

        if (count > 0) {
          hasDuplicate = true;
          finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }

        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
        }`;
        const formattedState = `${order.state} (D)`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

        const items = order.items;
        const orderLines = [];

        items.forEach((item, index) => {
          const itemNum = `${index + 1}-${items.length}`;
          const itemData = teePlusPackedItemData(item);
          const itemLine = `${itemNum}${itemData}`;

          if (index === 0) {
            orderLines.push(`${customerBase}\t\t${itemLine}`);
          } else {
            orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
          }
        });

        finalLines.push(...orderLines);

        const match = currentRef.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[0], 10);
          currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
        }
      } catch (err) {
        finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);

    const finalText = finalLines.join('\n');
    try {
      await copyOrderToTeePlus(finalText, nonCustomOrders.map((o) => o.orderNumber));
      toast.success(
        `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
        { autoClose: 6000 }
      );
    } catch (err) {
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleMarkAllAsOld = async () => {
    const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) لتحويلها إلى قديم");
      return;
    }

    const confirm = window.confirm(
      `هل تريد تحويل ${nonCustomOrders.length} طلب(ات) غير مخصصة إلى "قديم"؟\n\n` +
      `(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\n` +
      `الطلبات المحددة ستختفي من الصفحة فورًا.`
    );

    if (!confirm) return;

    let successCount = 0;
    let failedCount = 0;

    toast.info("جاري تحديث الطلبات غير المخصصة إلى قديم... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-old",
    });

    for (const order of nonCustomOrders) {
      try {
        await updateOrderStatus(order.orderNumber, "Old", "تم التحديث الجماعي بدون نسخ (غير مخصص)");
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    setLoadedOrders(prev => prev.filter(order => !nonCustomOrders.some(o => o.orderNumber === order.orderNumber)));

    toast.dismiss("marking-old");

    toast.success(
      `تم تحويل ${successCount} طلب(ات) غير مخصصة إلى قديم بنجاح!` +
      (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const incrementReference = () => {
    const match = currentReference.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[0], 10);
      const newNum = num + 1;
      const newRef = currentReference.replace(/\d+$/, newNum.toString());
      setCurrentReference(newRef);
      localStorage.setItem("orderReference", newRef);
    }
  };

  const updateReferenceFromInput = () => {
    if (referenceInput.trim() && referenceInput.includes(".")) {
      setCurrentReference(referenceInput.trim());
      localStorage.setItem("orderReference", referenceInput.trim());
    }
  };


  // فلترة الطلبات حسب البحث (مرحلة المتابعة تُصفّى من السيرفر)
  const filteredOrders = loadedOrders.filter((order) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  const activeFollowUpLabel = followUpFilter
    ? FOLLOW_UP_CHECKPOINTS.find((s) => s.value === followUpFilter)?.labelAr
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Package className="text-indigo-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              الطلبات الجديدة
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Stats Cards */}
        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Package className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص الطلبات الجديدة</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-indigo-600">{newOrdersSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{newOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Layers className="text-purple-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">إجمالي المنتجات المباعة</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="productsCard" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                    <span className="text-gray-600">عدد المنتجات</span>
                    <span className="text-2xl font-bold text-purple-600">{totals.totalProductsSold}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Message Buttons */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div> */}

        {/* Bulk Actions */}
        {isAdmin(authState.role) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Copy All */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-green-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-xl">
                  <CopyCheck className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل</h3>
                  <p className="text-sm text-green-700">
                    نسخ {loadedOrders.length} طلب مع فحص التكرار
                  </p>
                </div>
              </div>
              <button
                onClick={handleCopyAllVisibleOrders}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={20} />
                نسخ الكل
              </button>
            </div>
          </div>

          {/* Mark All as Old */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-purple-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Archive className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-purple-900 text-xl">تحويل الكل إلى قديم</h3>
                  <p className="text-sm text-purple-700">
                    تنظيف سريع لـ {loadedOrders.length} طلب
                  </p>
                </div>
              </div>
              <button
                onClick={handleMarkAllAsOld}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Archive size={20} />
                تحويل للقديم
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Reference Section */}
        {isAdmin(authState.role) && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-indigo-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label  className="block text-sm font-medium text-gray-700 mb-1 text-right">
                الرقم الحالي
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num + 1;
                      const newRef = currentReference.replace(/\d+$/, newNum.toString());
                      // تحديث الحالتين معاً
                      setCurrentReference(newRef);
                      setReferenceInput(newRef);
                      localStorage.setItem("orderReference", newRef);
                      // toast.success("تم زيادة الرقم!");
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-r-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="زيادة الرقم"
                >
                  <Plus size={20} />
                </button>
                <div className="flex-1 text-3xl font-bold text-indigo-600 bg-indigo-50 py-3 px-4 text-center border-2 border-indigo-200 ">
                  {currentReference}
                </div>
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num - 1;
                      if (newNum >= 0) {
                        const newRef = currentReference.replace(/\d+$/, newNum.toString());
                        // تحديث الحالتين معاً
                        setCurrentReference(newRef);
                        setReferenceInput(newRef);
                        localStorage.setItem("orderReference", newRef);
                        // toast.success("تم إنقاص الرقم!");
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-l-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="إنقاص الرقم"
                >
                  <Minus size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={() => {
                  if (referenceInput.trim() && referenceInput.includes(".")) {
                    setCurrentReference(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
                placeholder="Girabatcom.4000"
                style={{fontFamily:"cairo black !important"}}
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  setReferenceInput(referenceInput.trim()); // تأكيد التحديث
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
            <button
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ الكل ({loadedOrders.length})
            </button>
            <button
              onClick={handleMarkAllAsOld}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Archive size={20} />
              تحويل للقديم ({loadedOrders.length})
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-right">
            كل ما تنسخ بيانات عميل بعد "تحديث إلى قديم"، الرقم سيزيد تلقائيًا
          </p>
        </div>
        )}

        

        {/* Search and Filter Bar - الموجود */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
              />
            </div>
            <button
              type="button"
              onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                sortOrder === "desc"
                  ? "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                  : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
              }`}
              title={sortOrder === "desc" ? "الأحدث أولاً" : "الأقدم أولاً"}
            >
              <ArrowUpDown size={20} />
              {sortOrder === "desc" ? "الأحدث أولاً" : "الأقدم أولاً"}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                followUpFilter
                  ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Filter size={20} />
              تصفية
              {followUpFilter && (
                <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">1</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Filter size={18} className="text-indigo-600" />
                  تصفية حسب مرحلة المتابعة
                </h3>
                {followUpFilter && (
                  <button
                    type="button"
                    onClick={() => setFollowUpFilter(null)}
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 transition"
                  >
                    <X size={16} />
                    إلغاء التصفية
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setFollowUpFilter(null)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 text-right transition-all ${
                    !followUpFilter
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                      : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50"
                  }`}
                >
                  <span className="font-medium">كل المراحل</span>
                  <span
                    className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                      !followUpFilter ? "bg-white/20" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {isLoadingFollowUpCounts ? "…" : (followUpCounts.total ?? loadedOrders.length)}
                  </span>
                </button>

                {FOLLOW_UP_CHECKPOINTS.map((step) => {
                  const isActive = followUpFilter === step.value;
                  const count = followUpCounts.counts?.[step.value] ?? 0;
                  return (
                    <button
                      key={step.value}
                      type="button"
                      onClick={() => setFollowUpFilter(isActive ? null : step.value)}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 text-right transition-all ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                          : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50"
                      }`}
                    >
                      <span className="font-medium text-sm leading-snug">{step.labelAr}</span>
                      <span
                        className={`text-sm font-bold px-2 py-0.5 rounded-full flex-shrink-0 mr-2 ${
                          isActive ? "bg-white/20" : "bg-indigo-100 text-indigo-700"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {followUpFilter && (
                <p className="mt-3 text-sm text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
                  عرض {filteredOrders.length} طلب في مرحلة: <strong>{activeFollowUpLabel}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* قسم اختيار عدد العناصر لكل صفحة */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-2xl text-gray-600">
                (إجمالي المحمّل: {loadedOrders.length}
                {(followUpFilter || searchTerm) && ` · المعروض: ${filteredOrders.length}`})
              </span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-indigo-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        
        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <OrderMobileCardList
                    orders={filteredOrders}
                    expandedOrders={expandedOrders}
                    toggleExpand={toggleExpand}
                    borderClass="border-indigo-100"
                    dateClass="text-indigo-600"
                    productCardClass="bg-indigo-50 border-indigo-100"
                    productTitleClass="text-indigo-700"
                    productLinkClass="text-indigo-600 hover:text-indigo-800"
                    onDownload={handleDownload}
                    conversationPreset="new"
                    canSendConversationTemplates={canAct}
                    onPhoneClick={handleCopyPhoneNumber}
                    renderActions={(order) => (
                      <>
                        <OrderFollowUpTimeline
                          orderId={order.orderNumber}
                          checkpoint={order.followUpCheckpoint}
                          onUpdate={handleFollowUpCheckpointUpdate}
                          compact
                          readOnly={!canAct}
                          className="mb-3"
                        />
                      {canAct ? (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed", order)}
                          disabled={!orderCanEnterFulfillment(order)}
                          title={orderCanEnterFulfillment(order) ? undefined : "Payment Pending"}
                          className="bg-green-600 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <CheckCircle size={14} />
                          مؤكد
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(order.orderNumber, "Canceled", order)}
                          className="bg-red-600 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <XCircle size={14} />
                          ملغي
                        </button>
                        <PrimaryManualTemplateButton
                          order={order}
                          sendingId={sendingPrimaryTemplate1521}
                          onSend={handleSendPrimaryPhoneManualTemplate}
                          blocked={!orderCanEnterFulfillment(order)}
                          className="col-span-2 py-3 rounded-xl"
                        />
                        <SecondaryWhatsAppButton
                          order={order}
                          sendingId={sendingSecondaryWa}
                          onSend={handleSendSecondaryPhoneConfirmation}
                          blocked={!orderCanEnterFulfillment(order)}
                          className="col-span-2 py-3 rounded-xl"
                        />
                        <PrimaryWhatsApp1199Button
                          order={order}
                          sendingId={sendingPrimaryWa1199}
                          onSend={handleSendPrimaryPhoneConfirmation}
                          blocked={!orderCanEnterFulfillment(order)}
                          className="col-span-2 py-3 rounded-xl"
                        />
                        <StartConversationButton
                          order={order}
                          className="col-span-2 py-3 rounded-xl"
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                          className="bg-yellow-500 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <Clock size={14} />
                          معلق
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                          className="bg-purple-500 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <Edit size={14} />
                          تغييرات
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                          className="col-span-2 bg-gray-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <Archive size={14} />
                          قديم
                        </button>
                      </div>
                      ) : (
                        <p className="text-center text-sm text-slate-500 bg-slate-50 rounded-xl py-2 border border-slate-200">عرض فقط — بدون إجراءات</p>
                      )}
                      </>
                    )}
                    renderExpanded={(order) => (
                      <>
                        {isAdmin(authState.role) && (
                          <button
                            type="button"
                            onClick={() => handleCopyAllExcelData(order)}
                            disabled={!orderCanEnterFulfillment(order)}
                            title={orderCanEnterFulfillment(order) ? "نسخ (TeePlus)" : "Payment Pending"}
                            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Copy size={16} />
                            نسخ (TeePlus)
                          </button>
                        )}
                        {canAct && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                            className="bg-indigo-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <ShoppingCart size={13} />
                            تحت الشراء
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                            disabled={isPaidOnline(order)}
                            title={isPaidOnline(order) ? "تم الدفع أونلاين" : undefined}
                            className="bg-orange-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <DollarSign size={13} />
                            ديبوزت
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => handleDeleteOrder(order.orderNumber)}
                            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <Trash2 size={13} />
                            حذف
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <AlertTriangle size={13} />
                            Spam
                          </button>
                        </div>
                        )}
                      </>
                    )}
                  />

                  <table className="w-full min-w-[1200px] table-auto hidden md:table">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                        <th className="p-4 text-right font-bold text-indigo-800">رقم الطلب</th>
                        <th className="p-4 text-right font-bold text-indigo-800">العميل</th>
                        <th className="p-4 text-right font-bold text-indigo-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-indigo-800">الإجمالي</th>
                        {/* <th className="p-4 text-center font-bold text-indigo-800">#</th> */}
                        <th className="p-4 text-right font-bold text-indigo-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-indigo-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-indigo-800 min-w-[180px]">متابعة</th>
                        {/* {isAdmin(authState.role) && (
                          <th className="p-4 text-center font-bold text-indigo-800">نسخ Excel</th>
                        )} */}
                        <th className="p-4 text-center font-bold text-indigo-800">تأكيد</th>
                        <th className="p-4 text-center font-bold text-indigo-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-indigo-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          {/* Order Number */}
                          <td className="p-4">
                            <span className="font-mono font-bold text-indigo-600">#{order.orderNumber}</span>
                            <OrderPaymentBadge order={order} />
                          </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span style={{fontFamily : "cairo black"}} className="text-lg">{order.name}</span>
                            </div>

                          {/* Phone Numbers */}
                          <br />
                            <div className="space-y-1">
                              <button
                                type="button"
                                onClick={() => handleCopyPhoneNumber(order.phoneNumber)}
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 transition cursor-pointer"
                                title="نسخ الرقم"
                              >
                                <Phone size={14} />
                                {order.phoneNumber}
                              </button>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && order.phoneNumber2 !== "Unknown Phone" && (
                                <button
                                  type="button"
                                  onClick={() => handleCopyPhoneNumber(order.phoneNumber2)}
                                  className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm transition cursor-pointer"
                                  title="نسخ الرقم"
                                >
                                  <Phone size={12} />
                                  {order.phoneNumber2}
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                              <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                          </td>

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Item Numbers */}
                          {/* <td className="p-4 text-center">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="text-sm mb-1">
                                {item.quantityIndex}/{item.totalQuantity}
                              </div>
                            ))}
                          </td> */}

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  {/* Product Info */}
                                  <div className="flex-1">
                                    <div className="font-medium text-indigo-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                        {item.code}
                                      </span>
                                      
                                      {/* Drive Link Icon - جنب الكود مباشرة */}
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {/* Download button for CUSTOM items */}
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                              className="text-green-600 hover:text-green-800 transition-transform hover:scale-110"
                                              title="تحميل الصورة"
                                            >
                                              <Download size={16} />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Separator between items */}
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-2 max-w-[150px]">
                            <p  style={{fontFamily:"cairo black"}} className="text-lg text-gray-600 line-clamp-3">{order.note || "—"}</p>
                          </td>

                          {/* Follow-up checkpoint */}
                          <td className="p-2 align-top">
                            <OrderFollowUpTimeline
                              orderId={order.orderNumber}
                              checkpoint={order.followUpCheckpoint}
                              onUpdate={handleFollowUpCheckpointUpdate}
                              compact
                              readOnly={!canAct}
                            />
                          </td>

                          
                          {/* Confirm Actions */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              {isAdmin(authState.role) && (
                                <button
                                  onClick={() => handleCopyAllExcelData(order)}
                                  disabled={!orderCanEnterFulfillment(order)}
                                  title={orderCanEnterFulfillment(order) ? "نسخ (TeePlus)" : "Payment Pending"}
                                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                  <Copy size={16} />
                                  نسخ (TeePlus)
                                </button>
                              )}
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed", order)}
                                disabled={!orderCanEnterFulfillment(order)}
                                title={orderCanEnterFulfillment(order) ? undefined : "Payment Pending"}
                                className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle size={14} />
                                  مؤكد
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Canceled", order)}
                                className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <XCircle size={14} />
                                  ملغي
                                </div>
                              </button>
                              <PrimaryManualTemplateButton
                                order={order}
                                sendingId={sendingPrimaryTemplate1521}
                                onSend={handleSendPrimaryPhoneManualTemplate}
                                blocked={!orderCanEnterFulfillment(order)}
                                className="w-full"
                              />
                              <SecondaryWhatsAppButton
                                order={order}
                                sendingId={sendingSecondaryWa}
                                onSend={handleSendSecondaryPhoneConfirmation}
                                blocked={!orderCanEnterFulfillment(order)}
                                className="w-full"
                              />
                              <PrimaryWhatsApp1199Button
                                order={order}
                                sendingId={sendingPrimaryWa1199}
                                onSend={handleSendPrimaryPhoneConfirmation}
                                blocked={!orderCanEnterFulfillment(order)}
                                className="w-full"
                              />
                              <StartConversationButton
                                order={order}
                                className="w-full"
                              />
                              <ViewConversationButton
                                order={order}
                                templatePreset="new"
                                canSend={canAct}
                                className="w-full"
                              />
                            </div>
                            ) : (
                              <p className="text-sm text-slate-500 text-center">عرض فقط</p>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Edit size={14} />
                                  تغييرات
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                                className="bg-indigo-500 text-white px-3 py-2 rounded-lg hover:bg-indigo-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <ShoppingCart size={14} />
                                  تحت الشراء
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                                disabled={isPaidOnline(order)}
                                title={isPaidOnline(order) ? "تم الدفع أونلاين" : undefined}
                                className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <DollarSign size={14} />
                                  ديبوزت
                                </div>
                              </button>
                              {isAdmin(authState.role) && (
                                <button onClick={() => handleUpdateStatus(order.orderNumber, "Girabatcom")}
                                  className="flex items-center justify-center gap-1 bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 text-sm">
                                  <Building2 size={14} /> Girabatcom
                                </button>
                              )}
                            </div>
                            ) : null}
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Package className="mx-auto mb-4 text-gray-400" size={64} />
                  {followUpFilter || searchTerm ? (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات مطابقة للتصفية</p>
                      <p className="text-lg text-gray-500 mb-4">
                        {followUpFilter && activeFollowUpLabel
                          ? `لا يوجد طلبات في مرحلة: ${activeFollowUpLabel}`
                          : "جرّب تغيير معايير البحث أو التصفية"}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setFollowUpFilter(null);
                          setSearchTerm("");
                        }}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                      >
                        إلغاء التصفية
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات جديدة</p>
                      <p className="text-lg text-gray-500">سيتم عرض الطلبات الجديدة هنا</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Floating Button to Scroll to Top - أضف هذا الزر الجديد */}
        <div className="fixed bottom-6 right-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="العودة إلى الأعلى"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrdersPage;